import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApp } from '@/context/AppContext';
import { formatPrice } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminGstReport() {
  const { getAuthHeaders } = useApp();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ start_date: '', end_date: '' });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.start_date) params.append('start_date', range.start_date);
      if (range.end_date) params.append('end_date', range.end_date);
      const res = await axios.get(`${API_URL}/admin/gst-report?${params.toString()}`, { headers: getAuthHeaders() });
      setReport(res.data);
    } catch (error) {
      console.error('Error fetching GST report:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, range]);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    if (!report) return;
    const header = ['Order', 'Date', 'Customer', 'Subtotal (excl GST)', 'Shipping', 'GST', 'Total (incl GST)'];
    const rows = report.orders.map((o) => [
      o.order_number,
      o.date ? new Date(o.date).toLocaleDateString('en-NZ') : '',
      o.customer_name,
      o.subtotal, o.shipping_cost, o.gst_amount, o.total
    ]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gst-report-${range.start_date || 'all'}-${range.end_date || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatCard = ({ label, value, accent }) => (
    <div className="bg-white rounded-xl border border-border p-5">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || ''}`}>{value}</p>
    </div>
  );

  return (
    <div className="p-6" data-testid="admin-gst-report">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
          GST Report
        </h1>
        <p className="text-muted-foreground mt-1">GST collected on paid orders (NZ 15%)</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-border p-4 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <Label htmlFor="start_date">From</Label>
          <Input id="start_date" type="date" value={range.start_date} onChange={(e) => setRange({ ...range, start_date: e.target.value })} className="mt-1" data-testid="gst-start-date" />
        </div>
        <div>
          <Label htmlFor="end_date">To</Label>
          <Input id="end_date" type="date" value={range.end_date} onChange={(e) => setRange({ ...range, end_date: e.target.value })} className="mt-1" data-testid="gst-end-date" />
        </div>
        <Button onClick={fetchReport} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white" data-testid="run-gst-report-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Run Report
        </Button>
        <Button variant="outline" onClick={exportCsv} disabled={!report || report.order_count === 0} data-testid="export-gst-csv-btn">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Paid Orders" value={report.order_count} />
            <StatCard label="Sales (excl. GST)" value={formatPrice(report.total_sales_excl_gst)} />
            <StatCard label="GST Collected" value={formatPrice(report.total_gst_collected)} accent="text-[#EA580C]" />
            <StatCard label="Sales (incl. GST)" value={formatPrice(report.total_sales_incl_gst)} />
          </div>

          <div className="bg-white rounded-xl border border-border p-4 mb-6 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{report.business_name}</span>
            {report.gst_number ? <> · GST No: <span className="font-medium text-foreground">{report.gst_number}</span></> : <> · <span className="italic">GST number not set (add it in Settings)</span></>}
          </div>

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-left text-sm text-muted-foreground">
                    <th className="p-4">Order</th>
                    <th className="p-4">Date</th>
                    <th className="p-4">Customer</th>
                    <th className="p-4 text-right">Excl. GST</th>
                    <th className="p-4 text-right">Shipping</th>
                    <th className="p-4 text-right">GST</th>
                    <th className="p-4 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.orders.length === 0 ? (
                    <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      No paid orders in this period yet.
                    </td></tr>
                  ) : (
                    report.orders.map((o) => (
                      <tr key={o.order_number} className="border-b border-border last:border-0 hover:bg-muted/20 text-sm">
                        <td className="p-4 mono">{o.order_number}</td>
                        <td className="p-4">{o.date ? new Date(o.date).toLocaleDateString('en-NZ') : ''}</td>
                        <td className="p-4">{o.customer_name}</td>
                        <td className="p-4 text-right">{formatPrice(o.subtotal)}</td>
                        <td className="p-4 text-right">{formatPrice(o.shipping_cost)}</td>
                        <td className="p-4 text-right text-[#EA580C]">{formatPrice(o.gst_amount)}</td>
                        <td className="p-4 text-right font-medium">{formatPrice(o.total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
